import React, { useState, useEffect, useMemo } from 'react';
import { useStyletron } from 'baseui';
import { KIND as ButtonKind } from 'baseui/button';
import { Card } from 'baseui/card';
import { Tag } from 'baseui/tag';
import { Spinner } from 'baseui/spinner';
import { Notification, KIND } from 'baseui/notification';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton, SIZE, ROLE } from 'baseui/modal';
import { Room } from './types';
import { roomApi } from './api';
import EditRoomModal from './EditRoomModal';

const RoomList: React.FC = () => {
	const [css] = useStyletron();
	const [rooms, setRooms] = useState<Room[]>([]);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [notification, setNotification] = useState<{ message: string; kind: typeof KIND[keyof typeof KIND] } | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState<{ value: string; label: string }[]>([]);
	const [confirmEndRoomModal, setConfirmEndRoomModal] = useState<{ isOpen: boolean; roomId: string | null; roomTitle: string }>({
		isOpen: false,
		roomId: null,
		roomTitle: ''
	});
	const [confirmDeleteRoomModal, setConfirmDeleteRoomModal] = useState<{
		isOpen: boolean;
		hostRoomId: string | null;
		roomTitle: string;
	}>({
		isOpen: false,
		hostRoomId: null,
		roomTitle: ''
	});
	const [editRoomModal, setEditRoomModal] = useState<{ isOpen: boolean; room: Room | null }>({
		isOpen: false,
		room: null
	});
	const [openMenuRoomId, setOpenMenuRoomId] = useState<string | null>(null);

	const fetchRooms = async () => {
		try {
			setLoading(true);
			const response = await roomApi.listActiveRooms();
			if (response.success) {
				setRooms(response.data.rooms);
			} else {
				setNotification({ message: response.error || 'Failed to fetch rooms', kind: KIND.negative });
			}
		} catch (error: any) {
			setNotification({ message: 'Error fetching rooms', kind: KIND.negative });
		} finally {
			setLoading(false);
		}
	};

	// const handleEndRoom = (_id: string) => {
	// 	setOpenMenuRoomId(null); // Close menu
	// 	const room = rooms.find((r) => r._id === _id);
	// 	setConfirmEndRoomModal({
	// 		isOpen: true,
	// 		roomId: _id,
	// 		roomTitle: room?.title || ''
	// 	});
	// };

	const confirmEndRoom = async () => {
		if (!confirmEndRoomModal.roomId) return;

		try {
			setActionLoading(confirmEndRoomModal.roomId);
			const response = await roomApi.endRoom(confirmEndRoomModal.roomId);
			if (response.success) {
				setNotification({ message: 'Room ended successfully!', kind: KIND.positive });
				// Auto-dismiss the success notification after 3 seconds
				setTimeout(() => {
					setNotification(null);
				}, 3000);
				setConfirmEndRoomModal({ isOpen: false, roomId: null, roomTitle: '' });
				fetchRooms(); // Refresh the list
			} else {
				setNotification({ message: response.error || 'Failed to end room', kind: KIND.negative });
			}
		} catch (error: any) {
			setNotification({ message: 'Error ending room', kind: KIND.negative });
		} finally {
			setActionLoading(null);
		}
	};

	const closeConfirmModal = () => {
		setConfirmEndRoomModal({ isOpen: false, roomId: null, roomTitle: '' });
	};

	const handleDeleteRoom = (hostRoomId: string) => {
		setOpenMenuRoomId(null); // Close menu
		const room = rooms.find((r) => r._id === hostRoomId);
		setConfirmDeleteRoomModal({
			isOpen: true,
			hostRoomId,
			roomTitle: room?.title || ''
		});
	};

	const confirmDeleteRoom = async () => {
		if (!confirmDeleteRoomModal.hostRoomId) return;

		try {
			setActionLoading(confirmDeleteRoomModal.hostRoomId);
			const response = await roomApi.deleteRoom(confirmDeleteRoomModal.hostRoomId);
			if (response.success) {
				setNotification({ message: 'Room deleted successfully!', kind: KIND.positive });
				// Auto-dismiss the success notification after 3 seconds
				setTimeout(() => {
					setNotification(null);
				}, 3000);
				setConfirmDeleteRoomModal({ isOpen: false, hostRoomId: null, roomTitle: '' });
				fetchRooms(); // Refresh the list
			} else {
				setNotification({ message: response.error || 'Failed to delete room', kind: KIND.negative });
			}
		} catch (error: any) {
			setNotification({ message: 'Error deleting room', kind: KIND.negative });
		} finally {
			setActionLoading(null);
		}
	};

	const closeDeleteConfirmModal = () => {
		setConfirmDeleteRoomModal({ isOpen: false, hostRoomId: null, roomTitle: '' });
	};

	const handleEditRoom = (room: Room) => {
		setOpenMenuRoomId(null); // Close menu
		setEditRoomModal({
			isOpen: true,
			room
		});
	};

	const toggleMenu = (roomId: string) => {
		setOpenMenuRoomId(openMenuRoomId === roomId ? null : roomId);
	};

	const handleEditSuccess = () => {
		fetchRooms(); // Refresh the list
	};

	const closeEditModal = () => {
		setEditRoomModal({ isOpen: false, room: null });
	};

	// Filter and search logic
	const filteredRooms = useMemo(() => {
		let filtered = rooms;

		// Search filter
		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			const searchTermStr = searchTerm.toString(); // For numeric searches
			filtered = filtered.filter(
				(room) =>
					// String field searches
					(room.title?.toLowerCase() || '').includes(searchLower) ||
					(room.hostName?.toLowerCase() || '').includes(searchLower) ||
					(room._id?.toLowerCase() || '').includes(searchLower) ||
					(room.hostRoomId?.toLowerCase() || '').includes(searchLower) ||
					(room.uid?.toLowerCase() || '').includes(searchLower) ||
					(room.bio?.toLowerCase() || '').includes(searchLower) ||
					(room.pinnedMessageByHost?.toLowerCase() || '').includes(searchLower) ||
					(room.tags || []).some((tag) => (tag?.toLowerCase() || '').includes(searchLower)) ||
					(room.segmentNames || []).some((segment) => (segment?.toLowerCase() || '').includes(searchLower)) ||
					// Numeric field searches
					(room.hostId?.toString() || '').includes(searchTermStr) ||
					(room.membersAllowed?.toString() || '').includes(searchTermStr) ||
					(room.speakersAllowed?.toString() || '').includes(searchTermStr) ||
					(room.initialEntryFee?.toString() || '').includes(searchTermStr) ||
					(room.priorityOrder?.toString() || '').includes(searchTermStr) ||
					(room.timeAllowedForSpeaker?.toString() || '').includes(searchTermStr) ||
					// Badge searches
					(room.badge?.icon?.toLowerCase() || '').includes(searchLower) ||
					(room.badge?.text?.toLowerCase() || '').includes(searchLower) ||
					// Currency search
					(room.currencyType?.toLowerCase() || '').includes(searchLower) ||
					// Languages search
					(room.languages || []).some((lang) => (lang?.toLowerCase() || '').includes(searchLower)) ||
					// New fields search
					(room.charges?.toString() || '').includes(searchTermStr) ||
					(room.durationInMinutes?.toString() || '').includes(searchTermStr) ||
					(room.roomType?.toLowerCase() || '').includes(searchLower)
			);
		}

		// Status filter
		if (statusFilter.length > 0) {
			filtered = filtered.filter((room) => {
				const status = room.isActive ? 'active' : 'inactive';
				return statusFilter.some((filter) => filter.value === status);
			});
		}

		return filtered;
	}, [rooms, searchTerm, statusFilter]);

	// Get unique values for filter options
	const filterOptions = useMemo(() => {
		return {
			status: [
				{ value: 'active', label: 'Active' },
				{ value: 'inactive', label: 'Inactive' }
			]
		};
	}, []);

	useEffect(() => {
		fetchRooms();
		// Ensure modal state is reset on mount
		setEditRoomModal({ isOpen: false, room: null });
	}, []);

	if (loading) {
		return (
			<div className={css({ display: 'flex', justifyContent: 'center', padding: '40px' })}>
				<Spinner />
			</div>
		);
	}

	const renderTable = () => {
		const columns = [
			{ key: 'roomId', label: 'Room ID', width: '200px' },
			{ key: 'actions', label: 'Actions', width: '80px' },
			{ key: 'title', label: 'Title', width: '180px' },
			{ key: 'host', label: 'Host', width: '160px' },
			{ key: 'tags', label: 'Tags', width: '200px' },
			{ key: 'segmentNames', label: 'Segments', width: '200px' },
			{ key: 'status', label: 'Status', width: '100px' },
			{ key: 'priorityOrder', label: 'Priority', width: '80px' },
			{ key: 'badge', label: 'Badge', width: '120px' },
			{ key: 'thumbnail', label: 'Thumbnail', width: '150px' },
			{ key: 'hostProfilePic', label: 'Host Pic', width: '150px' },
			{ key: 'bio', label: 'Bio', width: '200px' },
			{ key: 'pinnedMessage', label: 'Pinned Message', width: '200px' },
			{ key: 'members', label: 'Members', width: '80px' },
			{ key: 'speakers', label: 'Speakers', width: '80px' },
			{ key: 'entryFee', label: 'Entry Fee', width: '90px' },
			{ key: 'currency', label: 'Currency', width: '80px' },
			{ key: 'timeAllowedForSpeaker', label: 'Speaker Time (s)', width: '120px' },
			{ key: 'languages', label: 'Languages', width: '150px' },
			{ key: 'charges', label: 'Charges', width: '80px' },
			{ key: 'durationInMinutes', label: 'Duration (min)', width: '100px' },
			{ key: 'roomType', label: 'Room Type', width: '100px' }
		];

		return (
			<div
				className={css({
					overflowX: 'auto',
					border: '1px solid #e8e8e8',
					borderRadius: '8px'
				})}>
				<table
					className={css({
						width: '100%',
						minWidth: '2830px',
						borderCollapse: 'collapse',
						backgroundColor: '#ffffff'
					})}>
					<thead>
						<tr
							className={css({
								backgroundColor: '#f8f9fa',
								borderBottom: '2px solid #e8e8e8'
							})}>
							{columns.map((column) => (
								<th
									key={column.key}
									className={css({
										padding: '16px 12px',
										textAlign: 'left',
										fontWeight: '600',
										fontSize: '0.9rem',
										color: '#374151',
										width: column.width,
										minWidth: column.width,
										position: 'sticky',
										top: 0,
										backgroundColor: '#f8f9fa',
										zIndex: 1
									})}>
									{column.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{filteredRooms.map((room) => (
							<tr
								key={room._id}
								className={css({
									borderBottom: '1px solid #e8e8e8',
									':hover': {
										backgroundColor: '#f8f9fa'
									}
								})}>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										fontFamily: 'monospace',
										color: '#6c757d',
										width: '200px',
										minWidth: '200px',
										wordBreak: 'break-all'
									})}>
									{room._id}
								</td>
								<td
									className={css({
										padding: '12px',
										width: '80px',
										minWidth: '80px',
										position: 'relative'
									})}>
									<div className={css({ display: 'flex', justifyContent: 'center' })}>
										<button
											type='button'
											onClick={() => toggleMenu(room._id)}
											disabled={actionLoading === room._id}
											className={css({
												background: 'transparent',
												border: 'none',
												cursor: actionLoading === room._id ? 'not-allowed' : 'pointer',
												padding: '8px',
												borderRadius: '4px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												opacity: actionLoading === room._id ? 0.5 : 1,
												':hover': {
													backgroundColor: actionLoading === room._id ? 'transparent' : '#f3f4f6'
												}
											})}>
											<span
												className={css({
													fontSize: '1.2rem',
													fontWeight: 'bold',
													color: '#6b7280'
												})}>
												⋮
											</span>
										</button>

										{/* Dropdown Menu */}
										{openMenuRoomId === room._id && (
											<>
												{/* Backdrop to close menu when clicking outside */}
												<div
													role='button'
													tabIndex={0}
													onClick={() => setOpenMenuRoomId(null)}
													onKeyDown={(e) => {
														if (e.key === 'Escape') {
															setOpenMenuRoomId(null);
														}
													}}
													aria-label='Close menu'
													className={css({
														position: 'fixed',
														top: 0,
														left: 0,
														right: 0,
														bottom: 0,
														zIndex: 999
													})}
												/>

												{/* Menu */}
												<div
													className={css({
														position: 'absolute',
														top: '50%',
														left: '100%',
														transform: 'translateY(-50%)',
														marginLeft: '8px',
														backgroundColor: '#ffffff',
														border: '1px solid #e5e7eb',
														borderRadius: '8px',
														boxShadow:
															'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
														zIndex: 1000,
														minWidth: '150px',
														overflow: 'hidden'
													})}>
													{/* Edit */}
													<button
														type='button'
														onClick={() => handleEditRoom(room)}
														disabled={actionLoading === room._id}
														className={css({
															width: '100%',
															padding: '10px 16px',
															border: 'none',
															background: 'transparent',
															textAlign: 'left',
															cursor: actionLoading === room._id ? 'not-allowed' : 'pointer',
															fontSize: '0.875rem',
															color: '#374151',
															display: 'flex',
															alignItems: 'center',
															gap: '8px',
															opacity: actionLoading === room._id ? 0.5 : 1,
															':hover': {
																backgroundColor:
																	actionLoading === room._id ? 'transparent' : '#f3f4f6'
															}
														})}>
														<span>✏️</span>
														<span>Edit</span>
													</button>

													{/* Delete */}
													<button
														type='button'
														onClick={() => handleDeleteRoom(room._id)}
														disabled={actionLoading === room._id}
														className={css({
															width: '100%',
															padding: '10px 16px',
															border: 'none',
															background: 'transparent',
															textAlign: 'left',
															cursor: actionLoading === room._id ? 'not-allowed' : 'pointer',
															fontSize: '0.875rem',
															color: '#dc2626',
															display: 'flex',
															alignItems: 'center',
															gap: '8px',
															opacity: actionLoading === room._id ? 0.5 : 1,
															borderTop: '1px solid #f3f4f6',
															':hover': {
																backgroundColor:
																	actionLoading === room._id ? 'transparent' : '#fef2f2'
															}
														})}>
														<span>🗑️</span>
														<span>Delete</span>
													</button>
												</div>
											</>
										)}
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										fontWeight: '500',
										color: '#2c3e50',
										width: '180px',
										minWidth: '180px'
									})}>
									{room.title}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										color: '#6c757d',
										width: '160px',
										minWidth: '160px'
									})}>
									<div>
										<div className={css({ fontWeight: '500', color: '#2c3e50' })}>{room.hostName}</div>
										<div className={css({ fontSize: '0.8rem', color: '#9ca3af' })}>ID: {room.hostId}</div>
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										width: '200px',
										minWidth: '200px'
									})}>
									<div className={css({ display: 'flex', gap: '4px', flexWrap: 'wrap' })}>
										{room.tags && room.tags.length > 0 ? (
											room.tags.map((tag) => (
												<Tag key={`${room._id}-tag-${tag}`} closeable={false} kind='neutral' size='small'>
													{tag}
												</Tag>
											))
										) : (
											<span
												className={css({
													fontSize: '0.75rem',
													color: '#9ca3af',
													fontStyle: 'italic'
												})}>
												-
											</span>
										)}
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										width: '200px',
										minWidth: '200px'
									})}>
									<div className={css({ display: 'flex', gap: '4px', flexWrap: 'wrap' })}>
										{room.segmentNames && room.segmentNames.length > 0 ? (
											room.segmentNames.map((segment) => (
												<Tag
													key={`${room._id}-segment-${segment}`}
													closeable={false}
													kind='primary'
													size='small'>
													{segment}
												</Tag>
											))
										) : (
											<span
												className={css({
													fontSize: '0.75rem',
													color: '#9ca3af',
													fontStyle: 'italic'
												})}>
												-
											</span>
										)}
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										width: '100px',
										minWidth: '100px'
									})}>
									<div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
										<div
											className={css({
												width: '8px',
												height: '8px',
												borderRadius: '50%',
												backgroundColor: room.isActive ? '#27ae60' : '#e74c3c'
											})}
										/>
										<span
											className={css({
												fontSize: '0.85rem',
												fontWeight: '500',
												color: room.isActive ? '#27ae60' : '#e74c3c'
											})}>
											{room.isActive ? 'Active' : 'Inactive'}
										</span>
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '80px',
										minWidth: '80px'
									})}>
									{room.priorityOrder ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										width: '120px',
										minWidth: '120px'
									})}>
									{room.badge && room.badge.text ? (
										<Tag closeable={false} kind='neutral' size='small'>
											{(() => {
												const badgeIcon = room.badge.icon || room.badge.bgUrl;
												return badgeIcon ? `${badgeIcon}: ${room.badge.text}` : room.badge.text;
											})()}
										</Tag>
									) : (
										<span className={css({ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' })}>-</span>
									)}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										color: '#6c757d',
										width: '150px',
										minWidth: '150px',
										wordBreak: 'break-all',
										maxWidth: '150px',
										overflow: 'hidden',
										textOverflow: 'ellipsis'
									})}>
									{room.thumbnail ? (
										<a
											href={room.thumbnail}
											target='_blank'
											rel='noopener noreferrer'
											className={css({
												color: '#007bff',
												textDecoration: 'underline'
											})}>
											View
										</a>
									) : (
										'-'
									)}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										color: '#6c757d',
										width: '150px',
										minWidth: '150px',
										wordBreak: 'break-all',
										maxWidth: '150px',
										overflow: 'hidden',
										textOverflow: 'ellipsis'
									})}>
									{room.hostProfilePic ? (
										<a
											href={room.hostProfilePic}
											target='_blank'
											rel='noopener noreferrer'
											className={css({
												color: '#007bff',
												textDecoration: 'underline'
											})}>
											View
										</a>
									) : (
										'-'
									)}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										color: '#6c757d',
										width: '200px',
										minWidth: '200px',
										maxWidth: '200px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									})}
									title={room.bio}>
									{room.bio || '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										color: '#6c757d',
										width: '200px',
										minWidth: '200px',
										maxWidth: '200px',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap'
									})}
									title={room.pinnedMessageByHost}>
									{room.pinnedMessageByHost || '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '80px',
										minWidth: '80px'
									})}>
									{room.membersAllowed ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '80px',
										minWidth: '80px'
									})}>
									{room.speakersAllowed ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '90px',
										minWidth: '90px'
									})}>
									{room.initialEntryFee ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										textAlign: 'center',
										fontWeight: '500',
										color: '#2c3e50',
										width: '80px',
										minWidth: '80px'
									})}>
									{room.currencyType ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '120px',
										minWidth: '120px'
									})}>
									{room.timeAllowedForSpeaker ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										width: '150px',
										minWidth: '150px'
									})}>
									<div className={css({ display: 'flex', gap: '4px', flexWrap: 'wrap' })}>
										{room.languages && room.languages.length > 0 ? (
											room.languages.map((lang) => (
												<Tag key={`${room._id}-lang-${lang}`} closeable={false} kind='positive' size='small'>
													{lang}
												</Tag>
											))
										) : (
											<span
												className={css({
													fontSize: '0.75rem',
													color: '#9ca3af',
													fontStyle: 'italic'
												})}>
												-
											</span>
										)}
									</div>
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '80px',
										minWidth: '80px'
									})}>
									{room.charges ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.9rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '100px',
										minWidth: '100px'
									})}>
									{room.durationInMinutes ?? '-'}
								</td>
								<td
									className={css({
										padding: '12px',
										fontSize: '0.85rem',
										textAlign: 'center',
										color: '#2c3e50',
										width: '100px',
										minWidth: '100px'
									})}>
									{room.roomType ? (
										<Tag closeable={false} kind={room.roomType === 'group' ? 'warning' : 'accent'} size='small'>
											{room.roomType === 'group' ? 'Group' : '1-on-1'}
										</Tag>
									) : (
										'-'
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	return (
		<div>
			{/* Confirmation Modal for End Room */}
			<Modal
				onClose={closeConfirmModal}
				closeable
				isOpen={confirmEndRoomModal.isOpen}
				animate
				autoFocus
				size={SIZE.default}
				role={ROLE.dialog}
				overrides={{
					Dialog: {
						style: {
							borderRadius: '12px'
						}
					}
				}}>
				<ModalHeader>Confirm End Room</ModalHeader>
				<ModalBody>
					<div className={css({ padding: '8px 0' })}>
						Are you sure you want to end the room <strong>&ldquo;{confirmEndRoomModal.roomTitle}&rdquo;</strong>?
						<br />
						<br />
						This action cannot be undone.
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalButton onClick={closeConfirmModal} kind={ButtonKind.tertiary}>
						Cancel
					</ModalButton>
					<ModalButton
						onClick={confirmEndRoom}
						kind={ButtonKind.primary}
						overrides={{
							BaseButton: {
								style: {
									backgroundColor: '#e74c3c',
									':hover': {
										backgroundColor: '#c0392b'
									}
								}
							}
						}}>
						End Room
					</ModalButton>
				</ModalFooter>
			</Modal>

			{/* Confirmation Modal for Delete Room */}
			<Modal
				onClose={closeDeleteConfirmModal}
				closeable
				isOpen={confirmDeleteRoomModal.isOpen}
				animate
				autoFocus
				size={SIZE.default}
				role={ROLE.dialog}
				overrides={{
					Dialog: {
						style: {
							borderRadius: '12px'
						}
					}
				}}>
				<ModalHeader>Confirm Delete Room</ModalHeader>
				<ModalBody>
					<div className={css({ padding: '8px 0' })}>
						Are you sure you want to <strong>permanently delete</strong> the room{' '}
						<strong>&ldquo;{confirmDeleteRoomModal.roomTitle}&rdquo;</strong>?
						<br />
						<br />
						<span className={css({ color: '#dc2626', fontWeight: '600' })}>
							Warning: This action will permanently remove the room from the hostrooms collection and cannot be undone!
						</span>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalButton onClick={closeDeleteConfirmModal} kind={ButtonKind.tertiary}>
						Cancel
					</ModalButton>
					<ModalButton
						onClick={confirmDeleteRoom}
						kind={ButtonKind.primary}
						overrides={{
							BaseButton: {
								style: {
									backgroundColor: '#dc2626',
									':hover': {
										backgroundColor: '#b91c1c'
									}
								}
							}
						}}>
						Delete Room
					</ModalButton>
				</ModalFooter>
			</Modal>

			{/* Edit Room Modal */}
			<EditRoomModal
				isOpen={editRoomModal.isOpen && editRoomModal.room !== null}
				onClose={closeEditModal}
				onSuccess={handleEditSuccess}
				room={editRoomModal.room}
			/>

			{/* Notifications */}
			{notification && (
				<div
					className={css({
						position: 'fixed',
						top: '20px',
						right: '20px',
						zIndex: 9999,
						minWidth: '300px',
						maxWidth: '400px'
					})}>
					<Notification
						kind={notification.kind}
						onClose={() => setNotification(null)}
						overrides={{
							Body: {
								style: {
									borderRadius: '12px',
									boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
									border: notification.kind === KIND.positive ? '1px solid #27ae60' : '1px solid #e74c3c'
								}
							}
						}}>
						{notification.message}
					</Notification>
				</div>
			)}

			{/* Rooms Table */}
			<Card
				overrides={{
					Root: {
						style: {
							backgroundColor: '#ffffff',
							borderRadius: '20px',
							boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
							border: '1px solid #e8e8e8'
						}
					},
					Contents: {
						style: {
							padding: '32px'
						}
					}
				}}>
				<div
					className={css({
						marginBottom: '24px',
						paddingBottom: '20px',
						borderBottom: '2px solid #f0f0f0',
						display: 'flex',
						alignItems: 'center',
						gap: '24px'
					})}>
					<h3
						className={css({
							fontSize: '1.5rem',
							fontWeight: '600',
							color: '#2c3e50',
							margin: '0',
							minWidth: 'fit-content'
						})}>
						Room Management
					</h3>

					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '4px'
						})}>
						<Input
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.currentTarget.value)}
							placeholder='Search...'
							clearable
							size='compact'
							overrides={{
								Root: {
									style: {
										borderRadius: '6px',
										width: '200px'
									}
								}
							}}
						/>
					</div>

					<div
						className={css({
							display: 'flex',
							alignItems: 'center',
							gap: '4px'
						})}>
						<span
							className={css({
								fontSize: '0.875rem',
								fontWeight: '500',
								color: '#374151',
								minWidth: 'fit-content'
							})}>
							Status:
						</span>
						<Select
							options={filterOptions.status}
							value={statusFilter}
							onChange={({ value }) => setStatusFilter(value as any)}
							placeholder='All'
							multi
							clearable
							size='compact'
							overrides={{
								Root: {
									style: {
										borderRadius: '6px',
										width: '140px'
									}
								}
							}}
						/>
					</div>

					<div
						className={css({
							marginLeft: 'auto',
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							backgroundColor: '#f8f9fa',
							padding: '6px 12px',
							borderRadius: '6px',
							border: '1px solid #e9ecef'
						})}>
						<span
							className={css({
								fontSize: '0.85rem',
								color: '#6c757d',
								fontWeight: '500'
							})}>
							Total:
						</span>
						<span
							className={css({
								fontSize: '0.9rem',
								color: '#2c3e50',
								fontWeight: '600'
							})}>
							{filteredRooms.length}
						</span>
					</div>
				</div>

				{filteredRooms.length === 0 ? (
					<div
						className={css({
							textAlign: 'center',
							padding: '40px',
							color: '#6c757d'
						})}>
						<p>{rooms.length === 0 ? 'No rooms found' : 'No rooms match your search criteria'}</p>
					</div>
				) : (
					renderTable()
				)}
			</Card>
		</div>
	);
};

export default RoomList;
